import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { stringify } from 'csv-stringify';
import { isEmpty } from 'lodash';
import { LOAD_SAVED_QUERIES, SET_ERRORS, CLEAR_ERRORS } from 'src/store/types';
import ContextChart from '../widgets/ContextChart.vue';
import OldContextGraph from '../widgets/ContextGraph_old.vue';
import ContextNetwork from '../widgets/ContextNetwork.vue';
import OldContextNetwork from '../widgets/ContextNetwork_old.vue';
import Excerpts from '../widgets/Excerpts.vue';
import NpsSummary from '../widgets/NpsSummary.vue';
import QueryBuilder from './QueryBuilder.vue';
import QueryRow from './QueryRow.vue';
import SentimentSummarySegment from '../widgets/SentimentSummarySegment.vue';
import TopicCorrelations from '../widgets/TopicCorrelations.vue';
import SegmentCorrelations from '../widgets/SegmentCorrelations.vue';
import TimelineTrend from '../widgets/TimelineTrend.vue';
import ModalPrompt from 'components/widgets/ModalPrompt.vue';
import Query from 'src/api/query';
import QueryUtils from 'src/utils/query';
import Utils from 'src/utils/general';
import FormatUtils from 'src/utils/formatters';
import { BfSpinner } from "components/Butterfly";
import { isQueryValid as isValid } from './utils';
export default Vue.extend({
    components: {
        BfSpinner,
        QueryBuilder,
        QueryRow,
        Excerpts,
        TimelineTrend,
        NpsSummary,
        SentimentSummarySegment,
        TopicCorrelations,
        SegmentCorrelations,
        ContextChart,
        OldContextGraph,
        ContextNetwork,
        OldContextNetwork,
        ModalPrompt,
    },
    beforeRouteEnter(to, from, next) {
        // Set the query from the url
        next(vm => {
            vm.$store.dispatch({
                type: LOAD_SAVED_QUERIES,
                projectId: vm.$route.params.projectId,
                analysisId: vm.$route.params.analysisId,
            }).then(() => {
                let urlQuery = vm.$route.query;
                if (!isEmpty(urlQuery)) {
                    if (urlQuery.savedQuery) {
                        vm.savedQueryId = Number(urlQuery.savedQuery);
                        // Make sure saved query is selected
                        vm.$nextTick(() => {
                            vm.$refs.queryBuilder.selectQuery(urlQuery.savedQuery);
                        });
                    }
                    vm.query = Utils.parseRouteQuery(urlQuery.q);
                    vm.queryScope = urlQuery.queryScope ?? vm.$store.getters.currentProject.query_scope_default;
                }
            });
        });
    },
    beforeRouteUpdate(to, from, next) {
        if (!this.allowRouteUpdateToExecuteQuery) {
            // First check whether we should do any work. It's possible the
            // route got updated by another function that already executed the
            // new query. If so, we won't run it here again. Note that that
            // other function is responsible managing the state of the "allow"
            // variable. We only check it here.
            //
            // Note that it is necessary that the other function use `await`
            // on the `router.push` call, which will return after the navigation
            // is completed so that the state variable can be reset.
            next();
            return;
        }
        // This guard can be triggered both by a query builder driven query update,
        // or by back/forwards navigation; we only want to catch the latter.
        // To do this we can tentatively parse the query from the new url and see if it
        // matches the currently stored one, and if so suppress the event.
        let query = Utils.parseRouteQuery(to.query.q);
        if (QueryUtils.areQueriesEquivalent(query, this.query) && to.query.savedQuery === this.savedQueryId) {
            // Suppress the event from a regular query change
        }
        else {
            // Continue by setting query value which will trigger page update
            this.query = query;
            if (to.query.savedQuery) {
                this.savedQueryId = to.query.savedQuery;
                // Make sure saved query is selected
                this.$refs.queryBuilder.selectQuery(to.query.savedQuery);
            }
            else {
                this.savedQueryId = null;
                this.$refs.queryBuilder.deselectSavedQuery();
            }
        }
        next();
    },
    data() {
        return {
            queryRef: null,
            query: [],
            recordHits: null,
            savedQueryId: null,
            queryLoading: false,
            exportingQuery: false,
            isQueryVisible: true,
            noResultQuery: false,
            hasDate: Utils.checkSchemaHasDate(this.$store.getters.currentProject.schema),
            networkModel: null,
            modelLoading: false,
            // Chart data for correlations charts
            topicsData: null,
            segmentsData: null,
            sentimentData: null,
            showDataExportLimitModal: false,
            exportLimit: 0,
            allowRouteUpdateToExecuteQuery: true,
            queryScopeOptions: [{
                    value: 'frame',
                    label: 'match on verbatims'
                }, {
                    value: 'sentence',
                    label: 'match on sentences'
                }],
            queryScope: this.$store.getters.currentProject?.query_scope_default ?? 'frame',
        };
    },
    computed: {
        ...mapGetters([
            'currentModel', 'hasNPS', 'hasSentiment', 'currentProject',
            'currentAnalysis', 'currentSite', 'savedQueries', 'featureFlags',
        ]),
        // Return if the current query is restricted to a single date
        isSingleDateQuery() {
            for (let q of this.query) {
                if (q.is_date && q.operator === 'is') {
                    return true;
                }
            }
            return false;
        },
        // Return first conflicting query, i.e a query containing the current saved query
        queryConflict() {
            if (!this.savedQuery)
                return false;
            const filter = (arr) => arr.filter((v) => !!v).flat();
            const recurse = (query) => (row) => {
                if (row.type === 'query') {
                    if (row.values.includes(this.savedQueryId.toString())) {
                        return query;
                    }
                    const children = row.values.map((value) => {
                        const query = this.savedQueries.find(({ id }) => id.toString() === value);
                        if (!query)
                            return null;
                        const rows = QueryUtils.botanicToQueryRows(query.query_value);
                        return rows.some(recurse(query)) ? query : null;
                    });
                    return filter(children)[0];
                }
                return null;
            };
            const conflicts = filter(this.query.map(recurse(this.savedQuery)));
            return conflicts[0];
        },
        // Returns false if the query has no rows, or any of the rows have no values.
        // We don't want to send off queries in an invalid state, which may
        // happen in the midst of user edits.
        isQueryValid() {
            return (this.query.length > 0
                && isValid(this.query)
                && !this.queryConflict);
        },
        firstRow() {
            // What index element should be our "first row"? Return the first row that isn't visible
            for (let [idx, q] of this.query.entries()) {
                if (q['type'] !== 'all_data') {
                    return idx;
                }
            }
            return undefined;
        },
        queryTerms() {
            let values = [];
            this.query.forEach((q) => {
                if (q.type === 'text' && q.operator === 'includes') {
                    q.values.forEach((v) => {
                        let term = this.currentModel.terms[v];
                        if (term && values.indexOf(term) < 0) {
                            values.push(term);
                        }
                    });
                }
            });
            return values;
        },
        botanicQuery() {
            return QueryUtils.queryRowsToBotanic(this.query, this.queryScope);
        },
        savedQuery() {
            return this.savedQueries.find(({ id }) => id === this.savedQueryId);
        }
    },
    metaInfo() {
        return {
            title: this.currentAnalysis ? `${this.currentAnalysis.name} Query - Kapiche` : null
        };
    },
    watch: {
        query: {
            async handler(query) {
                let urlQuery = { q: Utils.generateEncodedQuery(query) };
                if (this.savedQueryId !== null) {
                    urlQuery.savedQuery = this.savedQueryId;
                }
                if (this.queryScope) {
                    urlQuery.queryScope = this.queryScope;
                }
                if (query.length === 0 && (this.$route.query.q || '').length === 0 ||
                    urlQuery.q === this.$route.query.q) {
                    // Don't update the route if both the route and query are
                    // functionally equivalent, otherwise we get duplicates
                    // in our history stack which makes back/forward navigation janky.
                }
                else {
                    try {
                        // Using this data field to break a recursive cycle. This is the
                        // problematic cycle:
                        // 1. local code updates this.query
                        // 2. the watch for this.query (this function) runs and updates
                        //    the route.
                        // 3. Inside `beforeRouteUpdate`, it again updates this.query,
                        //    causing `executeQuery` to run again.
                        // We don't want this cycle. We only want the route update to
                        // execute the query if forward/back nav buttons change the route.
                        // See more explanation in the docstring for `beforeRouteUpdate`.
                        this.allowRouteUpdateToExecuteQuery = false;
                        await this.$router.push({ name: 'browse-excerpts', query: urlQuery });
                    }
                    finally {
                        this.allowRouteUpdateToExecuteQuery = true;
                    }
                }
                if (this.isQueryValid) {
                    await this.executeQuery(query);
                }
                else {
                    this.recordHits = null;
                }
            },
            deep: true
        }
    },
    updated() {
        // Set some styling so we are as dynamic as possible
        const searchContainer = this.$el.querySelector('.search-container');
        const queryContainer = this.$el.querySelector('.query-container');
        this.$nextTick(() => {
            searchContainer.style.top = `0px`;
            queryContainer.style.top = `${searchContainer.offsetHeight}px`;
        });
    },
    methods: {
        ...mapActions({ SET_ERRORS, CLEAR_ERRORS }),
        number: FormatUtils.number,
        // Quick passthrough for the botanic queries method
        botanicToQueryRows(query) {
            return QueryUtils.botanicToQueryRows(query);
        },
        changeQueryScope(newQueryScope) {
            this.queryScope = newQueryScope;
            this.executeQuery(this.query);
        },
        /**
         * Modify query row attributes
         * @param {number} index required.
         * @param {Object} change required.
         */
        updateRow(index, change) {
            this.query = this.query.map((v, i) => {
                return i === index
                    ? { ...this.query[index], ...change }
                    : v;
            });
        },
        // Delete query row at specified `index`
        deleteRow(index) {
            this.query.splice(index, 1);
            if (this.query.length === 0) {
                this.recordHits = null;
                // Clear selected saved query if the entire query is cleared
                this.$refs.queryBuilder.$refs.querySelector.$refs.savedQueries.clearSelection();
                this.savedQueryId = null;
            }
        },
        // Add a date (range) to query when clicked in the timeline widget.
        addDateToQuery(date, field, resolution) {
            this.query = this.query.concat(QueryUtils.generateDateQuery(date, field, resolution));
            this.$analytics.track.query.dateDrilldown(date.format('YYYY-MM-DD'), field, resolution);
        },
        // Add an nps type to query when clicked in the nps widget.
        addNpsToQuery(npsType) {
            this.query = this.query.concat({
                type: 'segment',
                field: 'NPS Category',
                operator: 'is',
                values: [npsType]
            });
            this.$analytics.track.query.npsDrilldown(npsType);
        },
        // Add a sentiment type to query when clicked in the sentiment widget.
        addSentimentToQuery(sentimentType) {
            this.query = this.query.concat({
                type: 'attribute',
                field: 'sentiment',
                operator: 'is',
                values: [sentimentType]
            });
            this.$analytics.track.query.sentimentDrilldown(sentimentType);
        },
        // Add a segment to query when clicked in the segment comparison widget.
        addSegmentToQuery(segmentName) {
            let field, value;
            [field, value] = segmentName.split('=');
            this.query = this.query.concat({
                type: 'segment',
                field: field,
                operator: 'is',
                values: [value]
            });
            this.$analytics.track.query.segmentDrilldown(field, value);
        },
        // Add term to query when selected in nested visualiation.
        addTermToQuery(termName, source) {
            this.query = this.query.concat({
                type: 'text',
                operator: 'includes',
                values: [termName]
            });
            this.$analytics.track.query.conceptDrilldown(termName, source);
        },
        // A unique query id that Vue uses to determine caching/updating of QueryRow components.
        // This is important because when we delete query rows, Vue will try to reuse components which can
        // lead to polluting our updated components with stale state.
        computeQueryId(queryRow) {
            if (!queryRow.id) {
                // We generate one id per row, on an ad hoc basis, allowing
                // us to easily handle the many ways new rows can be added.
                queryRow.id = Utils.uuid();
            }
            return queryRow.id;
        },
        // Execute the query
        executeQuery(query) {
            this.CLEAR_ERRORS();
            for (let q of query) {
                // Protect against not queries, which have an "all data" query which does not contain an operator
                if (q.hasOwnProperty('operator') && q.operator.endsWith('in the range') && q.values.length < 2) {
                    // Don't execute range queries until we have both values set
                    this.recordHits = 0;
                    this.noResultQuery = true;
                    return;
                }
            }
            this.queryLoading = true;
            const queryRef = Utils.uuid();
            this.queryRef = queryRef;
            // Outer query that blocks the page
            Query.runQuery(this.$route.params.projectId, this.$route.params.analysisId, this.botanicQuery, this.savedQueries, {
                limit: 0,
                documents: true,
                networkModel: false
            })
                .then((data) => {
                if (queryRef !== this.queryRef) {
                    // When our component-level queryRef doesn't match the method-scoped queryRef
                    // it means that a re-entrant query has happened and we are executing the callback
                    // for an old query. So we just bail out.
                    return;
                }
                this.recordHits = data.count;
                this.$analytics.track.query.run(JSON.stringify(this.botanicQuery), this.recordHits);
                // At this point the query is valid, so we set the query
                // This endpoint is MUCH FASTER than drilldown, so we do it here.
                this.noResultQuery = data.count <= 0;
            })
                .catch(errors => {
                this.SET_ERRORS({ errors });
                // The query is not valid, we don't pass anything to the child components
                this.noResultQuery = true;
            })
                .finally(() => {
                this.queryLoading = false;
            });
            this.generateContextNetwork(queryRef);
            let dataTypes = ['topics', 'segments', 'attributes'];
            dataTypes.forEach((dataType) => {
                Query.drilldown(this.currentProject.id, this.currentAnalysis.id, this.botanicQuery, dataType, 0, this.savedQueries)
                    .then(data => {
                    if (queryRef !== this.queryRef) {
                        // Callback for outdated query, so just bail out here
                        return;
                    }
                    if (dataType === 'attributes') {
                        // handle attributes specially - extract sentiment
                        if (data.counts.sentiment) {
                            this.sentimentData = {
                                counts: data.counts.sentiment,
                                numExcerpts: data.total_hits
                            };
                        }
                        else {
                            this.sentimentData = null;
                        }
                    }
                    else {
                        // segments or topics
                        this[`${dataType}Data`] = {
                            correlations: data.npmi,
                            counts: data.counts,
                            numExcerpts: data.total_hits
                        };
                    }
                })
                    .catch(errors => {
                    this.SET_ERRORS({ errors });
                });
            });
        },
        async generateContextNetwork(queryRef) {
            this.modelLoading = true;
            this.networkModel = null;
            let networkConfig = {
                num_network_concepts: 25
            };
            // Provide a baseline for determining influence;
            // Useful for calculating expected frequencies when
            // structured data filters are applied.
            const baselineQuery = QueryUtils.extractStructuredFiltersFromQuery(this.botanicQuery);
            networkConfig.baseline_query = JSON.stringify(baselineQuery);
            Query.generateContextNetwork(this.currentProject.id, this.currentAnalysis.id, this.currentSite.chrysalis_url, this.currentProject.chrysalis_ref, this.currentAnalysis.topic_framework_id, this.botanicQuery, this.savedQueries, networkConfig)
                .then((data) => {
                if (queryRef !== this.queryRef) {
                    // When our component-level queryRef doesn't match the method-scoped queryRef
                    // it means that a re-entrant query has happened and we are executing the callback
                    // for an old query. So we just bail out.
                    return;
                }
                this.networkModel = data.model;
            })
                .catch(errors => {
                this.SET_ERRORS({ errors });
            })
                .finally(() => {
                this.modelLoading = false;
            });
        },
        async exportQuery() {
            this.exportingQuery = true;
            try {
                const res = await Query.runQueryExport(this.$route.params.projectId, this.$route.params.analysisId, this.botanicQuery, this.savedQueries);
                stringify([res.headers].concat(res.rows), (_, csvString) => {
                    Utils.downloadCsv(csvString, 'query-result');
                });
                this.$analytics.track.analysis.downloadExport('Query Results', 'CSV', {
                    queryName: this.savedQuery?.name,
                    queryValue: JSON.stringify(this.botanicQuery)
                });
            }
            catch (e) {
                const exportLimit = e.body.match(/Limit is (\d+)/)?.[1];
                if (exportLimit) {
                    this.showDataExportLimitModal = true;
                    this.exportLimit = parseInt(exportLimit);
                }
            }
            finally {
                this.exportingQuery = false;
            }
        },
        // Handle query saved
        onQuerySaved(savedQueryId) {
            this.savedQueryId = savedQueryId;
            this.$router.push({ name: 'browse-excerpts', query: { q: this.$route.query.q, savedQuery: savedQueryId } });
        },
        // Handle saved query selection, bubbled up from QueryBuilder component
        onQuerySelected(savedQueryId, queryJson) {
            let queryRows = this.botanicToQueryRows(queryJson);
            this.savedQueryId = savedQueryId;
            this.query = queryRows;
            this.queryScope = queryJson.level || "frame";
            this.$refs.queryBuilder.selectQuery(savedQueryId);
        },
        // Remove references to the deleted query from the current state
        onQueryDeleted(queryId) {
            this.query = this.query.reduce((query, row) => {
                if (row.type === 'query') {
                    const values = row.values.filter((v) => v !== queryId.toString());
                    return values.length
                        ? query.concat({ ...row, values })
                        : query; // row is empty and is removed
                }
                return query.concat(row);
            }, []);
        },
        // Handle updated `query` from the QueryBuilder component
        onQueryUpdated(query) {
            this.query = query;
            if (query.length === 0) {
                // Clear selected saved query if the entire query is cleared
                this.$refs.queryBuilder.$refs.querySelector.$refs.savedQueries.clearSelection();
                this.queryScope = this.currentProject?.query_scope_default ?? "frame";
                this.savedQueryId = null;
                // Always make sure query is visible when it is empty
                this.isQueryVisible = true;
            }
        }
    }
});
